import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { AUTH_TOKEN_LOCAL_STORAGE_ITEM_NAME } from "@/utils/constants";
import { DEFAULT_LANGUAGE, LANGUAGES, writeStoredLanguage } from "./language";
import { namespaces, resources } from "./resources";

// Everything is bundled, so init is synchronous: the first render is already in
// the right language, and nothing suspends.
export function initI18n(language) {
	if (i18n.isInitialized) {
		i18n.changeLanguage(language);
		return i18n;
	}

	i18n.use(initReactI18next).init({
		lng: language,
		resources,
		ns: namespaces,
		defaultNS: "common",
		fallbackLng: DEFAULT_LANGUAGE,
		supportedLngs: LANGUAGES,
		// An empty English value falls back to Bosnian instead of rendering "".
		returnEmptyString: false,
		initAsync: false,
		interpolation: { escapeValue: false },
		react: { useSuspense: false },
	});

	return i18n;
}

export function getLanguage() {
	return i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LANGUAGE;
}

// Reading window.localStorage itself throws when site data is blocked.
export function browserStorage() {
	try {
		return typeof window === "undefined" ? null : window.localStorage;
	} catch {
		return null;
	}
}

export function hasAuthToken() {
	try {
		return Boolean(browserStorage()?.getItem(AUTH_TOKEN_LOCAL_STORAGE_ITEM_NAME));
	} catch {
		return false;
	}
}

// The one place the language changes. `persist: false` is for following a
// change another tab already stored.
export function applyLanguage(language, { persist = true } = {}) {
	i18n.changeLanguage(language);
	if (typeof document !== "undefined") document.documentElement.lang = language;
	if (persist) writeStoredLanguage(browserStorage(), language);
}

// While a switch is being saved to the account, a whoami response that raced
// it carries the old language and must not be applied.
let pendingSaves = 0;

export function beginSave() {
	pendingSaves += 1;
}

export function endSave() {
	pendingSaves = Math.max(0, pendingSaves - 1);
}

export function isSavePending() {
	return pendingSaves > 0;
}

export default i18n;
